import { AlertTriangle, Plus, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useState, type ReactNode } from 'react'

import {
  dayRowsToWindows,
  isHumanWorkingHoursNow,
  splitOvernightRange,
  workingHoursToDayRows,
  type WorkingHoursDayRow,
  type WorkingHoursRange,
} from '@aeris/communication'
import type { WorkingHours } from '@aeris/contracts'
import {
  Button,
  Combobox,
  DateField,
  SwitchField,
  type ComboboxItem,
} from '@aeris/ui'

/**
 * O construtor de horário — substitui os DOIS campos "cole um JSON aqui" que
 * o robô e a fila carregavam até a F058 ("que porra é essa de colocar json
 * não faz sentido" — o dono). Fica em `apps/web`, não em `packages/ui`:
 * precisa do tipo `WorkingHours` de `@aeris/contracts`, e o
 * dependency-cruiser proíbe `@aeris/ui` de importar contratos.
 *
 * Componente CONTROLADO (`value`/`onChange`), sem `react-hook-form` por
 * dentro — as duas telas que o usam (`atendimento-robo.tsx`,
 * `atendimento-filas.tsx`) já vivem num `useForm` próprio; este componente é
 * só mais um campo dentro dele, como `MoneyInput` ou `TagInput`.
 *
 * A conversão entre `windows` (o que o contrato grava) e "sete linhas, uma
 * por dia" (o que esta tela mostra) é função PURA em `@aeris/communication`
 * (`workingHoursToDayRows`/`dayRowsToWindows`/`splitOvernightRange`),
 * testada sem DOM — este arquivo só monta a interface em cima dela.
 */

const DAY_LABELS = [
  'Domingo',
  'Segunda-feira',
  'Terça-feira',
  'Quarta-feira',
  'Quinta-feira',
  'Sexta-feira',
  'Sábado',
] as const

const DEFAULT_TIMEZONE = 'America/Sao_Paulo'
const DEFAULT_RANGE: WorkingHoursRange = { start: '09:00', end: '18:00' }

/** Fusos brasileiros, no topo da lista — é o caso comum, e ninguém deveria rolar dez fusos americanos para achar o Brasil. */
const BRAZIL_TIME_ZONES = [
  'America/Sao_Paulo',
  'America/Manaus',
  'America/Belem',
  'America/Fortaleza',
  'America/Recife',
  'America/Bahia',
  'America/Cuiaba',
  'America/Campo_Grande',
  'America/Porto_Velho',
  'America/Boa_Vista',
  'America/Rio_Branco',
  'America/Noronha',
]

const allTimeZones = (): readonly string[] => {
  try {
    if (typeof Intl.supportedValuesOf === 'function') {
      const supported = Intl.supportedValuesOf('timeZone')
      const rest = supported.filter((tz) => !BRAZIL_TIME_ZONES.includes(tz))
      return [...BRAZIL_TIME_ZONES, ...rest]
    }
  } catch {
    // Falha aqui é o runtime não suportar `Intl.supportedValuesOf` — a
    // lista brasileira sozinha ainda cobre o caso comum.
  }
  return BRAZIL_TIME_ZONES
}

const ALL_TIME_ZONES = allTimeZones()

const timeZoneItems = (search: string): ComboboxItem[] => {
  const query = search.trim().toLowerCase()
  const filtered =
    query === ''
      ? ALL_TIME_ZONES
      : ALL_TIME_ZONES.filter((tz) => tz.toLowerCase().includes(query))
  return filtered.slice(0, 50).map((tz) => ({ value: tz, label: tz }))
}

export type WorkingHoursEditorProps = {
  value: WorkingHours | null
  onChange: (value: WorkingHours | null) => void
  /** Permite `value === null` — "herda o horário de outro lugar". Sem isto, `null` nunca é oferecido como escolha. */
  nullable?: boolean
  /** O rótulo do switch que liga um horário PRÓPRIO, quando `nullable`. */
  inheritLabel?: string
  disabled?: boolean
  error?: string
  /** Só para teste — o "agora" que a linha viva usa. Padrão: o relógio real, atualizado a cada minuto. */
  now?: Date
}

/** O rascunho de UMA faixa em edição — local até o `onBlur`, para não reestruturar as linhas (uma faixa pulando de dia) a cada tecla. */
type RangeDraft = {
  day: number
  index: number
  start: string
  end: string
}

export const WorkingHoursEditor = ({
  value,
  onChange,
  nullable = false,
  inheritLabel = 'Este item tem um horário próprio',
  disabled = false,
  error,
  now,
}: WorkingHoursEditorProps): ReactNode => {
  const [search, setSearch] = useState('')
  const [draft, setDraft] = useState<RangeDraft | null>(null)

  // O relógio da linha viva — atualizado a cada minuto, nunca a cada
  // segundo (não há motivo para redesenhar a tela mais rápido que o próprio
  // texto muda). `now` (prop) tem prioridade — é o que os testes usam para
  // um instante determinístico.
  const [tick, setTick] = useState(() => new Date())
  useEffect(() => {
    if (now !== undefined) return
    const interval = setInterval(() => {
      setTick(new Date())
    }, 60_000)
    return () => {
      clearInterval(interval)
    }
  }, [now])
  const nowValue = now ?? tick

  const hasOwnHours = value !== null
  const timezone = value?.timezone ?? DEFAULT_TIMEZONE

  const rows = useMemo(
    () => workingHoursToDayRows(value?.windows ?? []),
    [value]
  )
  const allDaysOff = hasOwnHours && rows.every((row) => row.ranges.length === 0)
  const isOpenNow = hasOwnHours && isHumanWorkingHoursNow(value, nowValue)

  const formattedNow = useMemo(() => {
    try {
      return new Intl.DateTimeFormat('pt-BR', {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
      }).format(nowValue)
    } catch {
      return '--:--'
    }
  }, [timezone, nowValue])

  const setDayRows = (nextRows: readonly WorkingHoursDayRow[]): void => {
    onChange({ timezone, windows: [...dayRowsToWindows(nextRows)] })
  }

  const setTimezone = (tz: string | null): void => {
    if (tz === null || tz.trim() === '') return
    onChange({ timezone: tz, windows: value?.windows ?? [] })
  }

  const toggleDay = (day: number, attends: boolean): void => {
    setDayRows(
      rows.map((row) =>
        row.day === day ? { day, ranges: attends ? [DEFAULT_RANGE] : [] } : row
      )
    )
  }

  const addRange = (day: number): void => {
    setDayRows(
      rows.map((row) =>
        row.day === day ? { day, ranges: [...row.ranges, DEFAULT_RANGE] } : row
      )
    )
  }

  const removeRange = (day: number, index: number): void => {
    setDayRows(
      rows.map((row) =>
        row.day === day
          ? { day, ranges: row.ranges.filter((_, i) => i !== index) }
          : row
      )
    )
  }

  const copyToWeekdays = (day: number): void => {
    const source = rows.find((row) => row.day === day)
    if (source === undefined) return
    setDayRows(
      rows.map((row) =>
        row.day >= 1 && row.day <= 5
          ? { day: row.day, ranges: source.ranges }
          : row
      )
    )
  }

  const copyToAllDays = (day: number): void => {
    const source = rows.find((row) => row.day === day)
    if (source === undefined) return
    setDayRows(rows.map((row) => ({ day: row.day, ranges: source.ranges })))
  }

  const rangeShown = (
    day: number,
    index: number,
    range: WorkingHoursRange
  ): WorkingHoursRange =>
    draft !== null && draft.day === day && draft.index === index
      ? { start: draft.start, end: draft.end }
      : range

  const editRange = (
    day: number,
    index: number,
    shown: WorkingHoursRange,
    field: 'start' | 'end',
    next: string
  ): void => {
    setDraft({
      day,
      index,
      start: field === 'start' ? next : shown.start,
      end: field === 'end' ? next : shown.end,
    })
  }

  /**
   * Aplica o rascunho ao sair do campo (blur) — nunca a cada tecla. É aqui
   * que uma faixa `22:00`–`02:00` vira DUAS janelas: `splitOvernightRange`
   * decide, e o resultado entra na linha do próprio dia (a primeira janela)
   * e na do dia seguinte (a segunda, se houver) — nunca tenta manter as
   * duas juntas na mesma linha.
   */
  const commitRange = (day: number): void => {
    if (draft?.day !== day) return
    const { index, start, end } = draft
    setDraft(null)
    if (start === '' || end === '') return

    const [first, second] = splitOvernightRange({ day, start, end })
    if (first === undefined) return

    setDayRows(
      rows.map((row) => {
        if (row.day === day) {
          const ranges = [...row.ranges]
          ranges[index] = { start: first.start, end: first.end }
          return { day, ranges }
        }
        if (second?.day === row.day) {
          return {
            day: row.day,
            ranges: [...row.ranges, { start: second.start, end: second.end }],
          }
        }
        return row
      })
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {nullable ? (
        <SwitchField
          disabled={disabled}
          label={inheritLabel}
          description={
            hasOwnHours
              ? undefined
              : 'Desligado: usa o horário configurado no robô.'
          }
          checked={hasOwnHours}
          onCheckedChange={(checked) => {
            onChange(
              checked ? { timezone: DEFAULT_TIMEZONE, windows: [] } : null
            )
          }}
        />
      ) : null}

      {!hasOwnHours ? null : (
        <div className="flex flex-col gap-3 rounded-(--radius-md) border border-(--border-subtle) p-3">
          <div className="flex flex-col gap-1">
            <span
              id="working-hours-timezone-label"
              className="text-(length:--text-xs) font-(--weight-medium) text-(--text-secondary)"
            >
              Fuso horário
            </span>
            <Combobox
              aria-describedby="working-hours-timezone-label"
              disabled={disabled}
              value={timezone}
              onValueChange={setTimezone}
              items={timeZoneItems(search)}
              search={search}
              onSearchChange={setSearch}
              selectedLabel={timezone}
              placeholder="Buscar fuso — ex.: São Paulo"
              emptyMessage="Nenhum fuso encontrado."
            />
          </div>

          <div className="flex flex-col gap-2">
            {rows.map((row) => {
              const attends = row.ranges.length > 0
              return (
                <div
                  key={row.day}
                  className="flex flex-col gap-2 rounded-(--radius-md) border border-(--border-subtle) p-2.5"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <SwitchField
                      disabled={disabled}
                      label={DAY_LABELS[row.day]}
                      checked={attends}
                      onCheckedChange={(checked) => {
                        toggleDay(row.day, checked)
                      }}
                    />

                    {attends ? (
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={disabled}
                          onClick={() => {
                            copyToWeekdays(row.day)
                          }}
                        >
                          Copiar para seg–sex
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={disabled}
                          onClick={() => {
                            copyToAllDays(row.day)
                          }}
                        >
                          Copiar para todos os dias
                        </Button>
                      </div>
                    ) : null}
                  </div>

                  {!attends ? null : (
                    <div className="flex flex-col gap-1.5 pl-1">
                      {row.ranges.map((range, index) => {
                        const shown = rangeShown(row.day, index, range)
                        return (
                          // A posição É a identidade de uma faixa dentro do
                          // dia — não há id próprio, e a lista só reordena
                          // por uma AÇÃO explícita (adicionar/remover), nunca
                          // por uma resposta de rede reordenando sozinha.
                          <div
                            key={`${String(row.day)}-${String(index)}`}
                            className="flex items-center gap-2"
                          >
                            <DateField
                              type="time"
                              disabled={disabled}
                              aria-label={`${DAY_LABELS[row.day]} — início da faixa ${String(index + 1)}`}
                              value={shown.start}
                              onChange={(event) => {
                                editRange(
                                  row.day,
                                  index,
                                  shown,
                                  'start',
                                  event.target.value
                                )
                              }}
                              onBlur={() => {
                                commitRange(row.day)
                              }}
                              className="w-32"
                            />
                            <span
                              aria-hidden="true"
                              className="text-(length:--text-sm) text-(--text-muted)"
                            >
                              até
                            </span>
                            <DateField
                              type="time"
                              disabled={disabled}
                              aria-label={`${DAY_LABELS[row.day]} — fim da faixa ${String(index + 1)}`}
                              value={
                                shown.end === '24:00' ? '23:59' : shown.end
                              }
                              // `<input type="time">` recusa "24:00" — mostramos
                              // "23:59" como aproximação visual, mas o VALOR
                              // gravado continua "24:00" até a pessoa editar
                              // este campo de novo (o `onChange` abaixo é o
                              // único jeito de trocar o valor gravado).
                              onChange={(event) => {
                                editRange(
                                  row.day,
                                  index,
                                  shown,
                                  'end',
                                  event.target.value
                                )
                              }}
                              onBlur={() => {
                                commitRange(row.day)
                              }}
                              className="w-32"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              disabled={disabled}
                              aria-label={`Remover faixa ${String(index + 1)} de ${DAY_LABELS[row.day]}`}
                              onClick={() => {
                                removeRange(row.day, index)
                              }}
                            >
                              <Trash2
                                aria-hidden="true"
                                className="h-3.5 w-3.5"
                              />
                            </Button>
                          </div>
                        )
                      })}

                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={disabled}
                        onClick={() => {
                          addRange(row.day)
                        }}
                        className="w-fit"
                      >
                        <Plus aria-hidden="true" className="h-3.5 w-3.5" />
                        Adicionar outra faixa (ex.: intervalo de almoço)
                      </Button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {!allDaysOff ? null : (
            <p className="flex items-start gap-2 rounded-(--radius-md) bg-(--warning-subtle) px-3 py-2 text-(length:--text-xs) text-(--warning)">
              <AlertTriangle
                aria-hidden="true"
                className="mt-0.5 h-3.5 w-3.5 shrink-0"
              />
              Nenhum dia marcado aqui NÃO significa "sempre fechado" — significa
              que não há horário humano definido, e o robô pode responder a
              qualquer hora.
            </p>
          )}

          <p
            role="status"
            className="text-(length:--text-xs) text-(--text-secondary)"
          >
            Agora são {formattedNow} em {timezone} —{' '}
            {isOpenNow
              ? 'este horário está ABERTO agora.'
              : 'este horário está FECHADO agora (o robô responde).'}
          </p>
        </div>
      )}

      {error === undefined ? null : (
        <p role="alert" className="text-(length:--text-xs) text-(--danger)">
          {error}
        </p>
      )}
    </div>
  )
}
